import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  Box,
  Text,
  useToast, 
  ModalFooter,
  useColorModeValue,
} from '@chakra-ui/react'
import React, { useEffect, useState, useCallback } from 'react' 
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useUpdateBlogPost } from 'queries/blogPost.query'
import Dropzone from '../../settings/components/Dropzone'
import { getStorageImageUrl } from 'utils/imageUrl'

const validationSchema = yup.object().shape({
  title: yup
    .string()
    .required('Title is required')
    .max(255, 'Title cannot be more than 255 characters'),
  slug: yup
    .string()
    .nullable()
    .max(255, 'Slug cannot be more than 255 characters'),
  excerpt: yup
    .string()
    .nullable()
    .max(500, 'Excerpt cannot be more than 500 characters'),
  content: yup.string().required('Content is required'),
  status: yup
    .string()
    .oneOf(['DRAFT', 'PUBLISHED'])
    .required('Status is required'),
  cover_image: yup
    .mixed()
    .nullable()
    .test(
      'is-file-valid',
      'Invalid file selected (check type/size, max 2MB)',
      value => {
        const file = value?.[0]
        if (!file) {
          return true
        }

        const validTypes = [
          'image/jpeg',
          'image/png',
          'image/jpg',
          'image/gif',
          'image/svg+xml',
        ]
        const maxSize = 2048 * 1024 // 2MB

        if (!validTypes.includes(file.type)) {
          return false
        }
        if (file.size > maxSize) {
          return false
        }
        return true
      },
    ),
  author: yup
    .string()
    .nullable()
    .max(255, 'Author name cannot be more than 255 characters'),
  read_time: yup
    .string()
    .nullable()
    .max(50, 'Read time cannot be more than 50 characters'),
  date: yup.date().nullable().typeError('Invalid date format'),
  category: yup
    .string()
    .oneOf(['concert', 'cultural', 'sports'], 'Invalid category selected')
    .required('Category is required'),
})

export function EditBlogPostModal ({ isOpen, onClose, blogPost }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')
  const toast = useToast()

  // State for a temporary file preview URL (local blob URL)
  const [localFilePreview, setLocalFilePreview] = useState(null)

  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    watch,
    setValue, 
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const newCoverImageFiles = watch('cover_image')
  const { mutate, isPending } = useUpdateBlogPost()

  // 1. Construct the URL for the existing blog post image
  const existingImageUrl = getStorageImageUrl(blogPost?.cover_image)

  // 2. Determine the final preview URL: local file (blob) overrides existing remote URL
  const previewImageUrl = localFilePreview || existingImageUrl

  // 3. Dropzone file change handler (Memoized for stability)
  const handleFileChange = useCallback(
    file => {
      // Revoke previous blob URL if exists
      if (localFilePreview) {
        URL.revokeObjectURL(localFilePreview)
      }

      if (file) {
        // Set value for react-hook-form (as an array with a single file object)
        setValue('cover_image', [file], { shouldValidate: true })
        // Set local state for preview
        setLocalFilePreview(URL.createObjectURL(file))
      } else {
        // Clear value and preview
        setValue('cover_image', null, { shouldValidate: true })
        setLocalFilePreview(null)
      }
    },
    [localFilePreview, setValue],
  )

  // 4. Cleanup local blob URL when localFilePreview changes or component unmounts
  useEffect(() => {
    return () => {
      if (localFilePreview) {
        URL.revokeObjectURL(localFilePreview)
      }
    }
  }, [localFilePreview])

  const onSubmit = formData => {
    const dataToSubmit = new FormData()

    dataToSubmit.append('_method', 'PUT')

    // Append all non-file/non-date fields
    dataToSubmit.append('id', blogPost.id)
    dataToSubmit.append('title', formData.title)
    dataToSubmit.append('slug', formData.slug || '')
    dataToSubmit.append('excerpt', formData.excerpt || '')
    dataToSubmit.append('content', formData.content)
    dataToSubmit.append('status', formData.status)
    dataToSubmit.append('author', formData.author || '')
    dataToSubmit.append('read_time', formData.read_time || '')
    dataToSubmit.append('category', formData.category)

    // Format and append date
    if (formData.date) {
      // Ensure date is formatted as YYYY-MM-DD string
      const date = new Date(formData.date)
      const formattedDate = date.toISOString().split('T')[0]
      dataToSubmit.append('date', formattedDate)
    } else {
      dataToSubmit.append('date', '')
    }

    // Conditionally append new cover image
    // newCoverImageFiles is the value watched from the form state, set by handleFileChange
    if (newCoverImageFiles && newCoverImageFiles.length > 0) {
      dataToSubmit.append('cover_image', newCoverImageFiles[0])
    }

    mutate(dataToSubmit, {
      onSuccess: () => {
        // Explicitly revoke URL and clear state after SUCCESS
        if (localFilePreview) {
          URL.revokeObjectURL(localFilePreview)
        }
        setLocalFilePreview(null)
        onClose()
        toast({
          title: 'Blog Post Updated.',
          description: 'The blog post has been successfully updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: err => {
        console.error('Update failed:', err)
        toast({
          title: 'Failed to Update Blog Post.',
          description:
            err.response?.data?.message ||
            'There was an error updating the blog post.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  // Effect to reset form when a new 'blogPost' prop is passed
  useEffect(() => {
    if (blogPost) {
      reset({
        ...blogPost,
        // Ensure the date is formatted for the input type="date"
        date: blogPost.date
          ? new Date(blogPost.date).toISOString().substring(0, 10)
          : '',
        // Always reset file input field to null when resetting the form
        cover_image: null,
      })
      // Clear the local preview state, the separate useEffect will revoke the old URL
      if (localFilePreview) {
        setLocalFilePreview(null)
      }
    }
  }, [blogPost, reset])

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Blog Post</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {blogPost && (
            <Box as='form' onSubmit={handleSubmit(onSubmit)} p={4}>
              <VStack spacing={4}>
                <FormControl id='title' isInvalid={errors.title}>
                  <FormLabel>Title</FormLabel>
                  <Input
                    {...register('title')}
                    color={inputColor}
                    backgroundColor={backgroundColor}
                  />
                  {errors.title && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.title.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id='excerpt' isInvalid={errors.excerpt}>
                  <FormLabel>Excerpt</FormLabel>
                  <Textarea
                    {...register('excerpt')}
                    color={inputColor}
                    backgroundColor={backgroundColor}
                  />
                  {errors.excerpt && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.excerpt.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id='content' isInvalid={errors.content}>
                  <FormLabel>Content</FormLabel>
                  <Textarea
                    {...register('content')}
                    color={inputColor}
                    backgroundColor={backgroundColor}
                  />
                  {errors.content && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.content.message}
                    </Text>
                  )}
                </FormControl>

                {/* --- DROPZONE INTEGRATION --- */}
                <FormControl id='cover_image' isInvalid={errors.cover_image}>
                  <FormLabel>Cover Image</FormLabel>
                  <Dropzone
                    title='Drag and drop new image here, or click to select'
                    onFileChange={handleFileChange}
                    preview={previewImageUrl} // Use the combined preview URL
                  />
                  {errors.cover_image && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.cover_image.message}
                    </Text>
                  )}
                </FormControl>
                {/* --- END DROPZONE INTEGRATION --- */}

                <FormControl id='author' isInvalid={errors.author}>
                  <FormLabel>Author</FormLabel>
                  <Input
                    {...register('author')}
                    color={inputColor}
                    backgroundColor={backgroundColor}
                  />
                  {errors.author && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.author.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id='read_time' isInvalid={errors.read_time}>
                  <FormLabel>Read Time</FormLabel>
                  <Input
                    {...register('read_time')}
                    color={inputColor}
                    backgroundColor={backgroundColor}
                  />
                  {errors.read_time && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.read_time.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id='date' isInvalid={errors.date}>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type='date'
                    {...register('date')}
                    color={inputColor}
                    backgroundColor={backgroundColor}
                  />
                  {errors.date && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.date.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id='category' isInvalid={errors.category}>
                  <FormLabel>Category</FormLabel>
                  <Select {...register('category')}>
                    <option value='concert'>Concert</option>
                    <option value='cultural'>Cultural</option>
                    <option value='sports'>Sports</option>
                  </Select>
                  {errors.category && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.category.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl id='status' isInvalid={errors.status}>
                  <FormLabel>Status</FormLabel>
                  <Select {...register('status')}>
                    <option value='DRAFT'>Draft</option>
                    <option value='PUBLISHED'>Published</option>
                  </Select>
                  {errors.status && (
                    <Text color='red.500' fontSize='sm'>
                      {errors.status.message}
                    </Text>
                  )}
                </FormControl>
              </VStack>
              <ModalFooter>
                <Button
                  colorScheme='blue'
                  type='submit'
                  isLoading={isPending}
                  isDisabled={isPending}
                >
                  Save
                </Button>
                <Button colorScheme='gray' ml={3} onClick={onClose}>
                  Cancel
                </Button>
              </ModalFooter>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
